import { safeRead, safeRemove, safeWrite } from './storage';
import { Logger } from 'wdc-cube';
import { NotAuthenticatedError, viewerTimeZone } from '../domain';
const LOG = Logger.get('ApiClient');
/**
 * A saida do painel para a rede, e a sessao que acompanha cada pedido.
 *
 * Existe separado dos servicos de tela por uma razao so: TODA area precisa destas
 * duas coisas, e nenhuma delas e de area nenhuma. Sem este lugar, cada servico
 * novo teria de repetir `baseUrl`, o cabecalho `Authorization`, a leitura do
 * armazenamento e - o que importa - a politica do 401/403, que e a parte que nao
 * pode divergir entre areas.
 *
 * A sessao mora aqui e nao num presenter porque quem sabe dela e quem a usa: o
 * token entra em toda requisicao autenticada, e quem descobre que ele morreu e
 * este objeto, ao ver a recusa.
 *
 * E o `ServiceLike` registrado no arranque - `service` no vocabulario do Cube, que
 * nao e o mesmo da pasta `*.api.ts` de cada assunto. Houve um `MormaiiService`
 * acima dele que era a unica saida do painel para a rede: mil linhas, toda area
 * escrevendo nela. Depois que cada assunto ganhou o seu `.api.ts`, o que sobrava
 * naquela classe era repasse - entrar virou `account.api.ts`, a chave do endereco
 * virou assunto do codec, e a sessao sempre foi daqui.
 */
export class ApiClient {
    constructor() {
        this.__initialized = false;
        /** Onde a API esta. O renderer escreve isto antes de `SingletonServices.start()`. */
        this.baseUrl = '/';
        /**
         * Enquanto a abertura confere o token guardado.
         *
         * Uma recusa ALI nao e sessao perdida no meio do uso: e o token velho sendo
         * descartado antes de qualquer tela existir, e `validateSession` ja cuida
         * dela. Avisar o presenter faria a aplicacao pedir login durante o proprio
         * boot.
         */
        this.validating = false;
    }
    static { this.INSTANCE = new ApiClient(); }
    /**
     * As mesmas chaves do painel antigo, de proposito: as duas aplicacoes
     * convivem durante a migracao, e um desenvolvedor que alterne entre elas nao
     * deveria ter de entrar de novo.
     */
    static { this.USER_KEY = '@galileo:user'; }
    static { this.TOKEN_KEY = '@galileo:token'; }
    // ========== CICLO DE VIDA ==========
    get name() {
        return 'api-client';
    }
    get initialized() {
        return this.__initialized;
    }
    async postConstruct() {
        this.restoreStoredSession();
        this.__initialized = true;
        LOG.info(`Initialized (baseUrl=${this.baseUrl})`);
    }
    async preDestroy() {
        this.__initialized = false;
    }
    // ========== SESSAO ==========
    get session() {
        return this.__session;
    }
    /**
     * Recupera a sessao guardada, se ainda nao houver uma em memoria.
     *
     * Idempotente, e publico porque o arranque precisa dela ANTES do
     * `SingletonServices.start()`: a chave do endereco tem de estar em maos antes do
     * `kickStart`, e busca-la exige o token. Chamar duas vezes nao custa nada.
     */
    restoreStoredSession() {
        if (!this.__session) {
            this.__session = this.readStoredSession();
        }
        return this.__session;
    }
    storeSession(session) {
        this.__session = session;
        safeWrite(ApiClient.USER_KEY, JSON.stringify(session));
        safeWrite(ApiClient.TOKEN_KEY, session.token);
    }
    clearSession() {
        this.__session = undefined;
        safeRemove(ApiClient.USER_KEY);
        safeRemove(ApiClient.TOKEN_KEY);
    }
    /**
     * Confirma no servidor que o token guardado ainda vale.
     *
     * O legado nao tem `GET /session`; o equivalente e `GET /user/find_current`,
     * que so responde com token vivo. Um token expirado no armazenamento e
     * indistinguivel de um valido ate alguem perguntar - e perguntar na abertura
     * evita a tela montar e cair no primeiro clique.
     */
    async validateSession() {
        if (!this.__session) {
            return undefined;
        }
        this.validating = true;
        try {
            await this.send('GET', '/user/find_current');
            return this.__session;
        }
        catch (caught) {
            if (caught instanceof NotAuthenticatedError) {
                this.clearSession();
                return undefined;
            }
            // Rede fora nao e sessao invalida: derrubar o login porque o servidor
            // nao respondeu obrigaria a entrar de novo a cada oscilacao.
            LOG.warn('Could not validate the stored session', caught);
            return this.__session;
        }
        finally {
            this.validating = false;
        }
    }
    readStoredSession() {
        const raw = safeRead(ApiClient.USER_KEY);
        if (!raw) {
            return undefined;
        }
        try {
            const parsed = JSON.parse(raw);
            if (!parsed.token || !Array.isArray(parsed.roles)) {
                return undefined;
            }
            return parsed;
        }
        catch {
            // Conteudo corrompido nao deve impedir a aplicacao de subir.
            safeRemove(ApiClient.USER_KEY);
            return undefined;
        }
    }
    // ========== TRANSPORTE ==========
    async send(method, path, body, signal, options) {
        /*
         * O painel se identifica em TODA chamada.
         *
         * E por este cabecalho que o backend aplica o bloqueio de empresa so ao SITE
         * (decisao do cliente de 08/10/2026): quem esta ligado so a empresas bloqueadas
         * nao entra aqui, e a sessao de quem ja estava dentro cai na proxima chamada. O
         * app nao manda o cabecalho, e nele nada bloqueia - ver `CompanyBlockService` no
         * backend.
         */
        const headers = {
            'X-Client': 'panel',
            /*
             * O fuso de quem olha. O banco esta em UTC, e e por este cabecalho que o
             * servidor sabe onde comeca e termina o "dia" de quem pede - o total do dia,
             * a janela do historico, o hoje do dashboard (ver `JanelaDoDia` no backend).
             */
            'X-Timezone': viewerTimeZone()
        };
        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        const token = options?.bearer ?? this.__session?.token;
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(this.url(path), {
            method,
            headers,
            signal,
            body: body === undefined ? undefined : JSON.stringify(body)
        });
        return this.answer(response, options?.credentialAtStake ?? false, options?.roleAtStake ?? false);
    }
    url(path) {
        const base = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
        return `${base}${path}`;
    }
    /**
     * Uma resposta, ou a excecao que ela merece.
     *
     * O 403 entra junto com o 401 porque o backend nao declara
     * `AuthenticationEntryPoint`: sem credencial ele cai no
     * `Http403ForbiddenEntryPoint` do Spring e responde 403 onde outro servico
     * responderia 401. Tratar so o 401 deixaria a sessao expirada passando por
     * falha generica.
     */
    async answer(response, credentialAtStake = false, roleAtStake = false) {
        if ((response.status === 401 || response.status === 403) && !credentialAtStake && !roleAtStake) {
            if (!this.validating) {
                this.onSessionLost?.();
            }
            throw new NotAuthenticatedError();
        }
        if (response.status === 204) {
            return undefined;
        }
        const text = await response.text();
        const payload = text ? safeParse(text) : undefined;
        if (!response.ok) {
            const message = payload?.message;
            const error = new Error(message ?? `${response.status} - não foi possível contatar o servidor.`);
            error.status = response.status;
            throw error;
        }
        return payload;
    }
}
function safeParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
//# sourceMappingURL=api-client.js.map